import { useMemo } from 'react';
import { BulkMessagingQueries } from '@frontend/api-bulk-messaging';
import { useHasBulkEmailAccess, useHasBulkTextAccess } from '@frontend/feature-access';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { createNavItem, createSubNavItems } from '../nav-item-utils';

export const useMarketingNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const { hasBulkEmailAccess } = useHasBulkEmailAccess();
  const { hasBulkTextAccess } = useHasBulkTextAccess();
  const { data: draftsCount = 0 } = BulkMessagingQueries.useListCampaignDrafts(
    {
      locationIds: selectedLocationIds,
      orgId: selectedOrgId,
      year: new Date().getFullYear(),
    },
    hasBulkEmailAccess,
    hasBulkTextAccess,
    {
      staleTime: 1000 * 60 * 5, // Keep drafts count fresh for 5 minutes to minimize refetching
      select: (campaigns) => campaigns.length, // Transform the count to reduce memory footprint
    }
  );

  const subNavItems = useMemo(
    () =>
      createSubNavItems([
        {
          label: t('All Campaigns'),
          relativePath: '/all-campaigns',
          trackingId: 'nav-marketing-all-campaings',
          description:
            'Lists all email and text marketing campaigns, both active and historical, with performance details for each.',
        },
        {
          label: t('Drafts'),
          relativePath: '/drafts',
          count: draftsCount,
          isPassiveCount: true,
          trackingId: 'nav-marketing-drafts',
          description: 'Shows marketing campaigns still in draft form, allowing further edits before launch.',
        },
      ]),
    [draftsCount, t]
  );

  return useMemo(
    () =>
      createNavItem('marketing', {
        hasCount: !!draftsCount,
        iconName: 'bulk-messaging',
        label: t('Marketing'),
        root: '/bulk',
        show: true,
        subNavItems,
      }),
    [draftsCount, subNavItems, t]
  );
};
